import { get, post } from '@/Utils/API';
import { AGGREGATION, AGGREGATION_ERROR, FILE, URL_DATASET } from '@/Utils/constants';
import { getFileUuidFromPath } from '@/Utils/getFileUuidFromPath';
import { ROW_NUMBER_COLUMN_ID } from '@/Utils/gridConstants';
import { datasetSliceName, spreadsheetSliceName } from '@/redux/constants';
import {
  setAggregationEndpointValues,
  setIsAggregationEndpointValuesLoading,
} from '@/redux/reducers/spreadsheetSlice';
import { useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useCurrentFile } from './useCurrentFile';
import { StatusCodes } from 'http-status-codes';

const useAggregationUpdater = (agGrid, isAggregationAllowed, isUpdatingModel, isInitialRender) => {
  const dispatch = useDispatch();

  const currentFile = useCurrentFile();
  const { isPreviewMode } = useSelector((state) => state[datasetSliceName]);
  const { clientState, aggregationEndpointValues, isInsertingNewColumn } = useSelector(
    (state) => state[spreadsheetSliceName]
  );

  const previousIsPivotEnabledRef = useRef(clientState.isPivotEnabled);

  const checkFileDeletion = async (fileUuid) => {
    const getMetadataResponse = await get(`${URL_DATASET}/${fileUuid}`, true);
    if (getMetadataResponse.status !== StatusCodes.OK) {
      return false;
    } else {
      const metadata = await getMetadataResponse.json();
      return metadata.Deleting !== 0;
    }
  };

  const getAggregationValues = async (aggregationPayload) => {
    dispatch(setIsAggregationEndpointValuesLoading(true));
    try {
      const fileUuid = getFileUuidFromPath();
      const isFileDeleted = await checkFileDeletion(fileUuid);
      if (isFileDeleted) {
        window.location.reload();
        return;
      }
      const endpoint = `${FILE}/${fileUuid}/${AGGREGATION}`;
      let aggregationValues = await post(endpoint, aggregationPayload);

      if (!aggregationValues.results) {
        aggregationValues = {
          results: aggregationPayload.ungroupedAggregations.map((e) => {
            return { column: e.id, value: AGGREGATION_ERROR };
          }),
        };
      }

      flushSync(() => dispatch(setAggregationEndpointValues(aggregationValues.results)));

      return aggregationValues;
    } catch (error) {
      throw new Error(error);
    } finally {
      dispatch(setIsAggregationEndpointValuesLoading(false));
    }
  };

  useEffect(() => {
    if (isUpdatingModel || isInitialRender) return;

    if (
      (isPreviewMode && !currentFile?.isFileShared && currentFile?.metadata?.FileSize) ||
      isAggregationAllowed.current
    )
      return;
    // Check if isPivotEnabled has been toggled
    const hasPivotBeenToggled = previousIsPivotEnabledRef.current !== clientState.isPivotEnabled;
    // Update the ref to the current value for the next render
    previousIsPivotEnabledRef.current = clientState.isPivotEnabled;
    if (
      !agGrid ||
      isInsertingNewColumn ||
      clientState.isPivotEnabled ||
      hasPivotBeenToggled ||
      isAggregationAllowed.current
    )
      return;
    const hasRowGroupColumns = agGrid.api.getRowGroupColumns()?.length > 0;

    const fetchAggregationValues = (ungroupedAggregations) => {
      const aggregationPayload = {
        ...agGrid.api.getGridOption('serverSideDatasource')?.state,
        sortModel: clientState.sortModel,
        filterModel: clientState.filterModel,
        ungroupedAggregations,
      };

      getAggregationValues(aggregationPayload);
    };

    const ungroupedAggregations = agGrid.api
      .getColumnState()
      ?.filter((el) => el.colId !== ROW_NUMBER_COLUMN_ID && el.aggFunc)
      .map((el) => ({
        id: el.colId,
        field: el.colId,
        displayName: agGrid.api.getColumnDef(el.colId).headerName,
        aggFunc: el.aggFunc,
      }));

    if ((aggregationEndpointValues && !hasRowGroupColumns) || hasRowGroupColumns) {
      fetchAggregationValues(ungroupedAggregations);
    }
  }, [clientState, agGrid, isInsertingNewColumn]);
};

export default useAggregationUpdater;
